import { Link } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTheme } from "../context/ThemeContext";

export default function CookiePolicy() {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'} transition-colors duration-300`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Cookie className={`w-8 h-8 ${isDarkMode ? 'text-[#00f2ea]' : 'text-cyan-600'}`} />
            <h1 className="text-3xl font-bold">Cookie Policy</h1>
          </div>
          
          <p className="opacity-60 mb-8">Last updated: December 21, 2025</p>

          <div className="space-y-8 opacity-90">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. What Are Cookies?</h2>
              <p className="leading-relaxed">
                Cookies are small text files that are stored on your device when you visit a website. They help websites remember your preferences, login information, and how you use the site. Cookies are widely used to make websites work more efficiently and provide a better user experience.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. How We Use Cookies</h2>
              <p className="mb-3">TheForecastTracker uses cookies for the following purposes:</p>
              
              <h3 className="font-medium mb-2">Essential Cookies</h3>
              <p className="mb-4">These cookies are necessary for the website to function properly. They enable core functionality such as:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>User authentication and session management</li>
                <li>Security features</li>
                <li>Remembering your login status</li>
              </ul>

              <h3 className="font-medium mb-2">Preference Cookies</h3>
              <p className="mb-4">These cookies remember your settings and preferences:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Theme preference (dark/light mode)</li>
                <li>Temperature unit preference (°F/°C)</li>
                <li>Default location settings</li>
                <li>Language preferences</li>
              </ul>

              <h3 className="font-medium mb-2">Analytics Cookies</h3>
              <p className="mb-4">These cookies help us understand how visitors use our website:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Pages visited and time spent</li>
                <li>Features used most frequently</li>
                <li>Error reports and performance data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Third-Party Cookies</h2>
              <p className="leading-relaxed mb-3">
                Some cookies on our site are placed by third-party services we use:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Analytics providers:</strong> To help us understand site usage</li>
                <li><strong>Map services:</strong> For radar and location features</li>
                <li><strong>Weather data providers:</strong> To deliver forecast information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Local Storage</h2>
              <p className="leading-relaxed">
                In addition to cookies, we use local storage (similar technology stored in your browser) to save your preferences and improve performance. This includes your theme setting, saved locations, and authentication tokens.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Managing Cookies</h2>
              <p className="leading-relaxed mb-3">
                You can control and manage cookies in several ways:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Browser Settings:</strong> Most browsers allow you to refuse cookies, delete existing cookies, or alert you when a cookie is set</li>
                <li><strong>Our Settings:</strong> You can manage certain preferences in your account settings</li>
                <li><strong>Third-Party Tools:</strong> Various browser extensions can help manage cookies</li>
              </ul>
              <p className="mt-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <strong>Note:</strong> Disabling essential cookies may prevent you from using certain features of our Service, such as staying logged in.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Cookie Retention</h2>
              <p className="leading-relaxed">
                Different cookies have different lifespans:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Persistent cookies:</strong> Remain until they expire or you delete them (typically 30 days to 1 year)</li>
                <li><strong>Authentication tokens:</strong> 7 days by default</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Updates to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions about our use of cookies, please contact us at:
              </p>
              <div className="mt-3 p-4 rounded-xl bg-white/5">
                <p><strong>TheForecastTracker</strong></p>
                <p>Email: privacy@theforecasttracker.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
